import * as React from 'react';
import { SpinnerWrapper, CustomButton, ButtonType, AlertMessage, AlertType } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { RiskPotReportApi } from '../../../api/RiskPotReportApi';
import { RiskPotReportTreatments } from './RiskPotReportTreatments';
import { PreAssessmentsTable } from './PreAssessmentsTable';
import { IRiskPotReport, IRiskPotReportPreAssessment } from '../../../models/IRiskPotReport';
import { useParams, useHistory } from 'react-router-dom';
import { setMessage } from '../../../helpers/MessageHelper';
import { RiskPotReportModal } from './RiskPotReportModal';
import { PreAssessmentModal } from './PreAssessmentModal';
import { IPatient } from '../../../models/IPatient';
import { RiskPotReportInformation } from "./RiskPotReportInformation";
import { RiskPotReportBudgetOverview } from './RiskPotReportBudgetOverview';
import { RiskPotReportHeader } from './RiskPotReportHeader';
import { RiskPotReportPreAssessmentApi } from '../../../api/RiskPotReportPreAssessmentApi';
import { RiskPotReportNotes } from './RiskPotReportNotes';
import { UserContext, isInRole } from '../../../contexts/UserContext';
import { ALLMÄNTANDVÅRDBAS, BEDÖMNINGSTANDLÄKARE } from '../../../utils/RoleConstants';
import { RiskPotReportAttachments } from './RiskPotReportAttachments';
import { EventLog } from "../../../components/EventLog/EventLog";

interface IRiskPotView {
    patient: IPatient;
}

export const RiskPotReport: React.FunctionComponent<IRiskPotView> = ({ patient }) => {
    const history = useHistory();
    const auth = useAuth();
    const riskPotReportApi = new RiskPotReportApi(auth);
    const riskPotReportPreAssessmentApi = new RiskPotReportPreAssessmentApi(auth);
    const [isFetchingRiskPotReport, setIsFetchingRiskPotReport] = React.useState<boolean>(false);
    const { id } = useParams<{ id: string }>();
    const [riskPotReport, setRiskPotReport] = React.useState<IRiskPotReport>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [showTreatmentForm, setShowTreatmentForm] = React.useState<boolean>(false);
    const [selectedPreAssessment, setSelectedPreAssessment] = React.useState<IRiskPotReportPreAssessment>();
    const [renderPreAssessmentForm, setRenderPreAssessmentForm] = React.useState<boolean>(true);
    const userInfo = React.useContext(UserContext);
    const hasRoleAllmanTandvard = isInRole(ALLMÄNTANDVÅRDBAS, userInfo.roles);
    const hasRoleBedomningsTandlakare = isInRole(BEDÖMNINGSTANDLÄKARE, userInfo.roles);

    React.useEffect(() => {
        getRiskPotReport(false);
    }, [])

    const getRiskPotReport = (refresh: boolean = true) => {
        if (id) {
            setIsFetchingRiskPotReport(true);
            riskPotReportApi.getRiskPotReport(id, refresh, (report: IRiskPotReport) => {
                setRiskPotReport(report);
            }, (error) => {
                const message = setMessage(error, errorMessage ?? "");
                setErrorMessage(message);
            }, () => {
                setIsFetchingRiskPotReport(false);
            });
        }
    }

    const onAddTreatmentSuccess = (message: string) => {
        changeSuccessMessage(message);
        setShowTreatmentForm(false);
        getRiskPotReport();
    }

    const changeSuccessMessage = (message: string) => {
        const newMessage = setMessage(message, successMessage ?? "");
        setSuccessMessage(newMessage);
    }

    const getPreAssessment = (preAssessmentId: number) => {
        setIsFetchingRiskPotReport(true);
        riskPotReportPreAssessmentApi.getPreAssessment(preAssessmentId.toString(),
            (data) => {
                setSelectedPreAssessment(data);
                document.getElementById("open-create-pre-assessment-modal")?.click();
            }, undefined, () => setIsFetchingRiskPotReport(false));
    }

    const clearSelectedPreAssessment = () => {
        setSelectedPreAssessment(undefined);
    }

    const isInRoleBedomningsTandlakareOrKonsultFHB = (): boolean => {
        return hasRoleBedomningsTandlakare;
    }

    const canAssessTreatments = (): boolean => {
        return isInRoleBedomningsTandlakareOrKonsultFHB() && riskPotReport !== undefined && riskPotReport.hasPreAssessmentAwaitingReview;
    }

    return (
        <SpinnerWrapper isLoading={isFetchingRiskPotReport}>
            <section className="card">
                <RiskPotReportHeader riskPotReport={riskPotReport} />
                <div className="card-body row">
                    <div className="col-12">
                        <AlertMessage duration={3000} alertType={AlertType.danger}>{errorMessage}</AlertMessage>
                        <AlertMessage duration={3000} alertType={AlertType.success}>{successMessage}</AlertMessage>
                        {
                            riskPotReport ?
                                <>
                                    <RiskPotReportInformation patient={patient} riskPotReport={riskPotReport} />
                                    <RiskPotReportNotes riskPotReportId={riskPotReport.riskPotReportId} riskPotReportNotes={riskPotReport.riskPotReportNotes} getRiskPotReport={getRiskPotReport} changeSuccessMessage={changeSuccessMessage} canAccessNotes={isInRoleBedomningsTandlakareOrKonsultFHB()} />
                                    <RiskPotReportAttachments getRiskPotReport={getRiskPotReport} attachments={riskPotReport.attachments} riskPotReportId={riskPotReport.riskPotReportId} canBeEdited={riskPotReport.canBeEdited} changeSuccessMessage={changeSuccessMessage} />
                                    <RiskPotReportTreatments treatments={riskPotReport.riskPotReportTreatments} riskPotReportCanBeEdited={riskPotReport.canBeEdited} canReportTreatment={riskPotReport.canReportTreatment} setShowTreatmentForm={setShowTreatmentForm} riskPotReportId={riskPotReport.riskPotReportId} changeSuccessMessage={changeSuccessMessage} getRiskPotReport={getRiskPotReport} canAssessTreatments={canAssessTreatments} riskPotReportStartDate={riskPotReport.assessmentStartDate} riskPotReportEndDate={riskPotReport.assessmentEndDate} riskPotReportinvoicedAmount={riskPotReport.invoicedAmount} getPreAssessment={getPreAssessment} activePreAssessmentDecisionComment={riskPotReport.activePreAssessmentDecisionComment} />
                                    <PreAssessmentsTable preAssessments={riskPotReport.preAssessments} getPreAssessment={getPreAssessment}
                                       canCreatePreAssessment={riskPotReport.canCreatePreAssessment} setRenderForm={setRenderPreAssessmentForm} />
                                    <RiskPotReportBudgetOverview billableAmount={riskPotReport.billableAmount} invoicedAmount={riskPotReport.invoicedAmount} />
                                    {
                                        riskPotReport?.events ?
                                            <>
                                                <hr />
                                                <EventLog id="referralEventLog" events={riskPotReport.events} />
                                            </>
                                            : null
                                    }
                                </>
                                : null
                        }
                    </div>
                </div>
                {
                    riskPotReport ?
                        <>
                            {
                                hasRoleAllmanTandvard
                                    ? <RiskPotReportModal onSuccess={onAddTreatmentSuccess} riskPotReportId={riskPotReport.riskPotReportId} riskPotReportStartDate={riskPotReport.assessmentStartDate} showTreatmentFormForm={showTreatmentForm} onCloseForm={() => setShowTreatmentForm(false)} riskPotReportEndDate={riskPotReport.assessmentEndDate} />
                                    : null
                            }
                            <PreAssessmentModal riskPotReportId={riskPotReport.riskPotReportId} 
                                getRiskPotReport={getRiskPotReport}
                                selectedPreAssessment={selectedPreAssessment} 
                                clearSelectedPreAssessment={clearSelectedPreAssessment} 
                                changeSuccessMessage={changeSuccessMessage} 
                                hasRoleAllmantandvard={hasRoleAllmanTandvard}
                                renderForm={renderPreAssessmentForm} 
                                setRenderForm={setRenderPreAssessmentForm} />
                        </>
                        : null
                }
                <div className="card-footer">
                    <div className="row">
                        <div className="col-6 text-left">
                            <CustomButton buttonType={ButtonType.general} onClick={() => history.goBack()} buttonText="Gå tillbaka" />
                        </div>
                    </div>
                </div>
            </section>
        </SpinnerWrapper >
    )
}